<script setup lang="ts">
  import {
    useApiRequestsAbort,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import { useCommonStore, useQuoteStore, useUserStore } from '@/shared/store'
  import {
    INSURENCE_VALUES_MULTI_PLAN_TEST_KEY,
    INSURENCE_VALUES_SINGLE_PLAN_TEST_KEY,
  } from '@/shared/constants'
  import { INSURANCE_VALUES_SP_TEST_VALUES } from './FlowSelectPlanView.constants'
  import { computed, defineAsyncComponent, onBeforeMount, ref } from 'vue'
  import { useAbTest } from '@/shared/composables/useAbTest'
  import { OutboundFlowRoutes } from '@/shared/types/router.types'
  import {
    type Country,
    CountryBoundType,
    FileCategory,
    type Plan,
    PromoCodeTypes,
    WordingDocName,
  } from '@/shared/types/api.types'
  import { useRouter } from 'vue-router'
  import { LOAD_EVENT_NAME } from '@/widgets/Flow/FlowSelectPlanWidget/FlowSelectPlanWidget.constants'
  import { useI18n } from 'vue-i18n'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'

  const emit = defineEmits<Emits>()

  const FlowSelectPlanWidget = defineAsyncComponent(() =>
    import('@/widgets/Flow').then((m) => m.FlowSelectPlanWidget)
  )

  const FlowSelectPlanWidgetInsuranceValues = defineAsyncComponent(() =>
    import('@/widgets/Flow').then((m) => m.FlowSelectPlanWidgetInsuranceValues)
  )

  interface Emits {
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
  }

  useApiRequestsAbort()
  const commonStore = useCommonStore()
  const userStore = useUserStore()
  const quoteStore = useQuoteStore()

  const { getAbTest } = useAbTest()
  const { push } = useRouter()
  const { getFormData } = useFormDataWithRedirect()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const gaService = useGoogleAnalytics()

  const { locale } = useI18n()

  const plans = ref<Plan[]>([])
  const isInsuranceValuesSelectPlan = ref(false)
  const policyWording = ref('')
  const listOfDestionations = ref<Country[]>([])
  const destinationOptions = ref<Country[]>([])

  const sendShowPlansAnalytics = () => {
    if (!plans.value || plans.value.length === 0) return

    const showPlansEventParams = plans.value.map((plan) => ({
      item_name: plan.name,
      price: plan.price,
      schemecode: plan.schemecode,
    }))

    gaService.sendOutboundSelectPlanShowPlans(showPlansEventParams)
  }

  const fetchQatarWordings = () =>
    commonStore
      .getQatarWordings(FileCategory.TRAVEL, WordingDocName.POLICY_WORDING)
      .then((data) => {
        policyWording.value = data[0]?.Link || ''
      })

  const fetchCountries = () =>
    userStore.fetchCountries(CountryBoundType.OUTBUND, locale.value).then((data) => {
      listOfDestionations.value = data
      destinationOptions.value = listOfDestionations.value
    })

  const getQuote = async (): Promise<void> => {
    const destinationIds = userStore.destination.map((country) => country?.id)
    if (!destinationIds.length) {
      await push({ name: OutboundFlowRoutes.DESTINATION })
      return
    }
    let type: PromoCodeTypes | undefined
    if (userStore.isAffinity && userStore.promocode) {
      type = PromoCodeTypes.AFFINITY
    }
    const quote = await quoteStore.getTravelQuoteUae(
      userStore.promocode ?? '',
      0,
      destinationIds,
      type
    )
    if (quote.schemes.length === 0) {
      return Promise.reject('quote.schemes.length === 0')
    } else {
      plans.value = quote.schemes
      quoteStore.setProductBonus(quote.productBonus, true)
      quoteStore.setCoinsDiscountPrice(quote.coinsDiscountPrice, true)
      quoteStore.setQuoteWithCoinsDiscount(quote.quoteWithCoinsDiscount, true)
      sendShowPlansAnalytics()
    }
  }

  const abTestKey = computed(() =>
    plans.value.length > 1
      ? INSURENCE_VALUES_MULTI_PLAN_TEST_KEY
      : INSURENCE_VALUES_SINGLE_PLAN_TEST_KEY
  )

  onBeforeMount(async () => {
    emit(LOAD_EVENT_NAME, async () => {
      if (!(await getFormData(TravelDirection.OUTBOUND, false))) return
      await Promise.all([getQuote(), fetchQatarWordings(), fetchCountries()])

      await getAbTest(abTestKey.value)
      isInsuranceValuesSelectPlan.value = INSURANCE_VALUES_SP_TEST_VALUES.includes(
        commonStore.getAbValue(abTestKey.value)
      )

      onPerformanceCheckEnd()
    })
  })
</script>

<template>
  <component
    :is="isInsuranceValuesSelectPlan ? FlowSelectPlanWidgetInsuranceValues : FlowSelectPlanWidget"
    :plans="plans"
    :policy-wording="policyWording"
    :list-of-destionations="listOfDestionations"
  />
</template>
